import {
  Tag,
  Box,
  Button,
  ButtonGroup,
  LegacyCard,
  Checkbox,
  HorizontalStack,
  Text,
  VerticalStack,
  TextField,
  Label,
  Select,
} from '@shopify/polaris';
import {
  CirclePlusOutlineMinor
} from '@shopify/polaris-icons';
import { TCustomerConfig } from './types/TCustomerConfig';
import { useState, useCallback } from 'react';

export const Form = (props: {
  setInEditMode: Function,
  isSubmitting: boolean,
  saveAppConfig: Function,
  draftCustomerTagConfig: TCustomerConfig["tags"],
  draftCustomerMetafieldConfig: TCustomerConfig["metafields"],
  onTagPropertyUpdated: Function,
  onMetafieldPropertyUpdated: Function,
  hasError: boolean,
}) => {
  const {
    isSubmitting,
    onTagPropertyUpdated,
    onMetafieldPropertyUpdated,
    draftCustomerTagConfig,
    draftCustomerMetafieldConfig,
    setInEditMode,
  } = props;

  const [tagToAdd, setTagToAdd] = useState<string>('');
  const [tagError, setTagError] = useState<string>('');

  const addTag = useCallback(() => {
    // input validate
    if (tagToAdd.match(/^[a-zA-Z0-9:-]+$/) === null) {
      setTagError("Only letters and numbers are allowed");
      return;
    }
    // at least 3 characters
    if (tagToAdd.length < 3) {
      setTagError("A tag prefix should at least contain 3 characters");
      return;
    }

    if (!draftCustomerTagConfig.tagPrefixes.includes(tagToAdd)) {
      onTagPropertyUpdated('tagPrefixes', [
        ...draftCustomerTagConfig.tagPrefixes,
        tagToAdd,
      ]);
      setTagToAdd("");
      setTagError("");
    } else {
      setTagError("Tag prefix is already present");
    }
  }, [draftCustomerTagConfig.tagPrefixes, tagToAdd, onTagPropertyUpdated]);

  // get tags and action to update
  const tagPrefixes = draftCustomerTagConfig.tagPrefixes.map((prefix) =>
    <Box padding="1" as="span" key={prefix}>
      <Tag key={prefix} children={prefix} onRemove={() => {
        // copy because splice is destructive
        const newPrefixes = [
          ...draftCustomerTagConfig.tagPrefixes,
        ];
        const index = newPrefixes.indexOf(prefix);
        newPrefixes.splice(index, 1);

        onTagPropertyUpdated('tagPrefixes', newPrefixes);
      }} />
    </Box>
    );

  const [metafieldNamespaceToAdd, setMetafieldNamespaceToAdd] = useState<string>('');
  const [namespaceError, setNamespaceError] = useState<string>('');
  const [metafieldKeyToAdd, setMetafieldKeyToAdd] = useState<string>('');
  const [keyError, setKeyError] = useState<string>("");
  const [metafieldTypeToAdd, setMetafieldTypeToAdd] = useState<string>('single_line_text_field');

  const addMetafield = useCallback(() => {
    // input validate
    if (metafieldNamespaceToAdd.match(/^[a-zA-Z0-9:-_]+$/) === null) {
      setNamespaceError("Only letters and numbers are allowed");
      return;
    }
    // at least 3 characters
    if (metafieldNamespaceToAdd.length < 3) {
      setNamespaceError("A metafield namespace should at least contain 3 characters");
      return;
    }

    if (metafieldKeyToAdd.match(/^[a-zA-Z0-9:-_]+$/) === null) {
      setKeyError("Only letters and numbers are allowed");
      return;
    }
    if (metafieldKeyToAdd.length < 3) {
      setKeyError(
        "A metafield key should at least contain 3 characters"
      );
      return;
    }

    if (
      draftCustomerMetafieldConfig.allowedMetafields.filter(
        (metafield) =>
          metafield.namespace === metafieldNamespaceToAdd && metafield.key === metafieldKeyToAdd
      ).length === 0
    ) {
      onMetafieldPropertyUpdated("allowedMetafields", [
        ...draftCustomerMetafieldConfig.allowedMetafields,
        {
          namespace: metafieldNamespaceToAdd,
          key: metafieldKeyToAdd,
          type: metafieldTypeToAdd,
        },
      ]);
      setMetafieldNamespaceToAdd("");
      setNamespaceError("");

      setMetafieldKeyToAdd("");
      setKeyError("");

      setMetafieldTypeToAdd("single_line_text_field");
    } else {
      setNamespaceError("Metafield is already present");
    }
  }, [
    draftCustomerMetafieldConfig.allowedMetafields,
    metafieldNamespaceToAdd,
    metafieldKeyToAdd,
    metafieldTypeToAdd,
    onMetafieldPropertyUpdated,
  ]);

  const metafields = draftCustomerMetafieldConfig.allowedMetafields.map(({namespace, key, type}) => (
    <Box padding="1" as="span" key={`${namespace}-${key}`}>
      <Tag
        key={`${namespace}:${key}`}
        children={`${namespace}:${key} (${type})`}
        onRemove={() => {
          // copy because splice is destructive
          const newMetafields = [...draftCustomerMetafieldConfig.allowedMetafields];
          const index = newMetafields.findIndex(metafield => metafield.namespace === namespace && metafield.key === key);
          newMetafields.splice(index, 1);

          onMetafieldPropertyUpdated("allowedMetafields", newMetafields);
        }}
      />
    </Box>
  ));

  return (
    <LegacyCard>
      <LegacyCard.Section>
        <VerticalStack gap="2">
          <p>
            <Text variant="bodyMd" as="span" fontWeight="semibold">
              Tags
            </Text>
          </p>
          <p>
            <Checkbox
              checked={draftCustomerTagConfig.enabled}
              label="Enabled"
              onChange={() =>
                onTagPropertyUpdated("enabled", !draftCustomerTagConfig.enabled)
              }
            />
          </p>
        </VerticalStack>
      </LegacyCard.Section>
      {draftCustomerTagConfig.enabled && (
        <>
          <LegacyCard.Section>
            <HorizontalStack blockAlign="center" gap="2">
              <Label children="Add tag prefix" id="label-tag-prefix" />
              <div style={{ width: "400px" }}>
                <TextField
                  autoComplete=""
                  value={tagToAdd}
                  error={tagError}
                  min={3}
                  max={30}
                  minLength={3}
                  maxLength={30}
                  onChange={setTagToAdd}
                  label=""
                  id="label-tag-prefix"
                />
              </div>
              <Button
                outline
                onClick={() => addTag()}
                icon={CirclePlusOutlineMinor}
              />
            </HorizontalStack>
          </LegacyCard.Section>
          {tagPrefixes.length > 0 && (
            <LegacyCard.Section>
              <VerticalStack gap="2">
                <p>
                  <Text variant="bodyMd" as="span" fontWeight="semibold">
                    Allowed tag prefixes
                  </Text>
                </p>
                <p>{tagPrefixes}</p>
              </VerticalStack>
            </LegacyCard.Section>
          )}
        </>
      )}

      {/* Metafields */}

      <LegacyCard.Section>
        <VerticalStack gap="2">
          <p>
            <Text variant="bodyMd" as="span" fontWeight="semibold">
              Metafields
            </Text>
          </p>
          <p>
            <Checkbox
              checked={draftCustomerMetafieldConfig.enabled}
              label="Enabled"
              onChange={() =>
                onMetafieldPropertyUpdated(
                  "enabled",
                  !draftCustomerMetafieldConfig.enabled
                )
              }
            />
          </p>
        </VerticalStack>
      </LegacyCard.Section>
      {draftCustomerMetafieldConfig.enabled && (
        <>
          <LegacyCard.Section>
            <p>
              <Text variant="bodyMd" as="span" fontWeight="semibold">
                Add allowed metafield
              </Text>
            </p>
            <HorizontalStack blockAlign="center" gap="2">
              <div>
                <TextField
                  autoComplete=""
                  placeholder="Namespace: Sample"
                  value={metafieldNamespaceToAdd}
                  error={namespaceError}
                  min={3}
                  max={30}
                  minLength={3}
                  maxLength={30}
                  onChange={setMetafieldNamespaceToAdd}
                  label=""
                  id="label-metafield-namespace"
                />
              </div>
              <div>
                <TextField
                  placeholder="Key: Test"
                  autoComplete=""
                  value={metafieldKeyToAdd}
                  error={keyError}
                  min={3}
                  max={30}
                  minLength={3}
                  maxLength={30}
                  onChange={setMetafieldKeyToAdd}
                  label=""
                  id="label-metafield-key"
                />
              </div>
              <div>
                <Select
                  onChange={setMetafieldTypeToAdd}
                  id="label-metafield-type"
                  label=""
                  value={metafieldTypeToAdd}
                  options={[
                    {
                      label: "Single Line Text Field",
                      value: "single_line_text_field",
                    },
                    {
                      label: "List: Single Line Text Field",
                      value: "list.single_line_text_field",
                    },
                  ]}
                />
              </div>
              <Button
                outline
                onClick={() => addMetafield()}
                icon={CirclePlusOutlineMinor}
              />
            </HorizontalStack>
          </LegacyCard.Section>
          {metafields.length > 0 && (
            <LegacyCard.Section>
              <VerticalStack gap="2">
                <p>
                  <Text variant="bodyMd" as="span" fontWeight="semibold">
                    Allowed metafields
                  </Text>
                </p>
                <p>{metafields}</p>
              </VerticalStack>
            </LegacyCard.Section>
          )}
        </>
      )}

      <LegacyCard.Section subdued>
        <ButtonGroup>
          <Button
            children="Cancel"
            loading={props.isSubmitting}
            onClick={() => setInEditMode(false)}
          />
          <Button submit primary children="Save" loading={isSubmitting} />
        </ButtonGroup>
      </LegacyCard.Section>
    </LegacyCard>
  );
};
